/* eslint-disable react/prop-types */
import React from "react";
import ReactDOM from "react-dom";
import { push } from "react-router-redux";
import { connect } from "react-redux";
import { debounce } from 'lodash';
import { useDebounce } from 'ahooks';
import { Button, Input, List, Modal } from 'antd';
import styled from "styled-components";
import { CheckOutlined } from '@ant-design/icons';

import Icon from "metabase/components/Icon";
import OnClickOutsideWrapper from "metabase/components/OnClickOutsideWrapper";
import { getFgaProject, getUser } from "metabase/selectors/user";
import { isSearch } from '../../ab/utils/utils';
import { loadCurrentFgaProject } from "metabase/redux/user";
import SearchResultsModal from "./SearchResultsModal";
import { getGames } from "metabase/selectors/control";

import "./SearchBar.css";
import { Avatar } from 'antd';

const SearchWrapper = styled.div`
  position: relative;
  border-radius: 8px;
  border: ${props => props.isDark ? "1px solid #484D55" : "1px solid #dcdee4"};
  flex: 1 1 auto;
  display: flex;
  column-gap: 8px;
  width: 400px;
  height: 36px;
  padding-left: 10px;
  align-items: center;
  color: ${props => props.isDark ? "#A0AEC0" : "white"};
  transition: all 300ms ease-in;
  background-color: ${props => props.isDark ? "transparent" : "#f9f9f9"};

  ${props => props.active && `
    border-color: ${props.isDark ? "#FFFFFF88" : "#000000"};
  `}
`;

const SearchInput = styled.input`
  background-color: transparent;
  height: 100%;
  width: 100%;
  border: none;
  padding: 0 10px 0 0;
  color: ${props => props.isDark ? "#fff" : "#000"};
  font-size: 1em;
  font-weight: 500;
  &:focus {
    outline: none;
  }
  &::placeholder {
    color: #adadad;
  }
`;

const ProjectList = styled(List)`
  max-height: 360px;
  overflow-y: auto;
  background: ${props => props.isDark ? '#242427' : '#fff'};
  border-radius: 4px;
  padding: 8px 6px 8px 8px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.isDark ? '#353945' : '#f0f0f0'};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.isDark ? '#4A5568' : '#ccc'};
    border-radius: 3px;
    
    &:hover {
      background: ${props => props.isDark ? '#718096' : '#999'};
    }
  }
  
  .ant-list-item {
    padding: 2px 10px;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    margin: 2px 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: 4px;
    
    &:hover {
      background-color: ${props => props.isDark ? '#353945' : '#f5f5f5'};
    }

    &.selected {
      background-color: ${props => props.isDark ? '#ffffff20' : '#E6F7FF'};
    }

    .project-info {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .project-icon {
      width: 20px;
      height: 20px;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: ${props => props.isDark ? '#353945' : '#f0f0f0'};
      flex-shrink: 0;
    }

    .project-details {
      display: flex;
      flex-direction: column;
    }

    .project-name {
      max-width: 160px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: ${props => props.isDark ? '#ECEEF1' : '#000'};
    }

    .project-role {
      font-size: 12px;
      color: ${props => props.isDark ? '#74FFCD' : '#52c41a'};
    }
  }
`;

const ALLOWED_SEARCH_FOCUS_ELEMENTS = new Set(["BODY", "A"]);

class SearchBar extends React.Component {
  state = {
    active: false,
    searchText: "",
    searchTextDebounced: "",
    activeTab: "dashboard",
    projectFilter: "",
  };

  UNSAFE_componentWillMount() {
    this._updateSearchTextFromUrl(this.props);
    window.addEventListener("keyup", this.handleKeyUp);
  }

  componentWillUnmount() {
    window.removeEventListener("keyup", this.handleKeyUp);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.location?.pathname !== nextProps.location?.pathname) {
      this._updateSearchTextFromUrl(nextProps);
    }
    // deactivate search on navigation
    if (this.props.location !== nextProps.location) {
      this.setState({ active: false });
    }
  }

  _updateSearchTextFromUrl(props) {
    const components = props.location?.pathname.split("/");
    if (components[components.length - 1] === "search") {
      this.setState({ searchText: props.location.query.q });
    } else {
      this.setState({ searchText: "" });
      this.searchTextDebounced("")
    }
  }

  handleKeyUp = e => {
    const FORWARD_SLASH_KEY = 191;
    if (
      e.keyCode === FORWARD_SLASH_KEY &&
      ALLOWED_SEARCH_FOCUS_ELEMENTS.has(document.activeElement.tagName)
    ) {
      ReactDOM.findDOMNode(this.searchInput).focus();
      this.setState({ active: true });
    }
  };

  activeTabCallback = tab => {
    if (tab) {
      this.setState({
        activeTab: tab,
      });
    }
  };

  tagOnClick = tag => {
    this.setState({ searchTextDebounced: tag, searchText: tag });
  };

  searchTextDebounced = debounce(text => {
    this.setState({ searchTextDebounced: text });
  }, 1000);

  getProjectRole = (projectName) => {
    const { user } = this.props;
    if (user?.isTestTeamFeature415 || user?.isTestTeamFeature416) {
      const roles = user?.roles;
      const projectId = this.calProjectId(projectName);
      const projectRoles = roles?.filter(r => this.calProjectId(r.roleName) === projectId);
      if (projectRoles?.find(r => r.role === "Manager")) {
        return "Project Admin";
      } else if (projectRoles?.find(r => r.role === "Data User")) {
        return "Normal Access";
      }
      return "View Only";
    }
    if (user?.role === "ecosystem") {
      return "Project Admin";
    } else if (user?.role === "general") {
      return "View Only";
    }
    return "View Only";
  }

  calProjectId = (str) => {
    const match = str?.match(/\d+/);
    return match ? parseInt(match[0], 10) : null;
  }

  render() {
    const { active, searchText, searchTextDebounced } = this.state;
    const { isDark, project } = this.props;
    return (
      <div>

      <OnClickOutsideWrapper
        handleDismissal={() => this.setState({ active: false })}
      >
        <SearchWrapper
          onClick={() => this.setState({ active: true })}
          active={active}
          isDark={isDark}
        >
          <Icon name="search" ml={["10px", 2]} className="text-medium" style={{color: active ? '#EFEEF1' : '#959DA9'}} />
          <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <SearchInput
              py={1}
              pr={[0, 2]} 
              pl={1}
              ref={ref => (this.searchInput = ref)}
              isDark={isDark}
              value={searchText}
              maxLength={200}
              placeholder={"Search"}
              onClick={() => this.setState({ active: true })}
              onChange={e => {
                this.setState({ searchText: e.target.value })
                this.searchTextDebounced(e.target.value.trim())
              }}
              onKeyDown={e => this.setState({ active: true })}
              style={{ flex: 1 }}
            />
            {searchText && (
              <Button
                type="text"
                size="small"
                icon={<Icon name="close" size={12} />}
                onClick={(e) => {
                  e.stopPropagation();
                  this.setState({ searchText: '', searchTextDebounced: '' });
                }}
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '4px',
                  color: isDark ? '#A0AEC0' : '#666',
                  background: 'transparent'
                }}
              />
            )}
          </div>
          {active && (
            <SearchResultsModal
              projectId={project?.id}
              searchText={searchTextDebounced}
              activeTabCallback={this.activeTabCallback}
              loadCurrentFgaProject={this.props.loadCurrentFgaProject}
              tagOnClick={this.tagOnClick}
              gameList={this.props.gameList}
              project={this.props.project}
            />
          )}
        </SearchWrapper>

        
      </OnClickOutsideWrapper>
      </div>
    );
  }
}

export default connect(state => ({ 
  user: getUser(state),
  project: getFgaProject(state),
  gameList: getGames(state),
}), { push, loadCurrentFgaProject })(SearchBar);
